import { differenceInHours, formatDistance, isValid } from "date-fns";
import { format } from "date-fns-tz";
import { getValueByPointer } from "fast-json-patch";
import i18n, { type TFunction } from "i18next";

import {
  DATE_WITH_TIME_AND_ZONE_FORMAT,
  DATE_WITH_TIME_FORMAT,
  DATE_WITH_TIME_NO_SECONDS,
  DISPLAY_DATE_FORMATS,
  DisplayDateFormat,
  HOUR_ONLY,
  ISO_FORMAT,
  LONG_DATE_FORMAT,
  MAX_FILTER_ID,
  MIN_FILTER_ID,
  MONTH_DAY,
  MONTH_DAY_NUMERIC,
  MONTH_ONLY,
  MONTH_YEAR,
  MONTH_YEAR_DAY,
  MONTH_YEAR_FULL,
  MONTH_YEAR_SHORT,
  SHORT_DATE_FORMAT,
  TIME_AGO_THRESHOLD_HOURS,
  TIME_ONLY,
  TIME_WITH_SECONDS,
  VERY_SHORT_DATE_FORMAT,
  Weekday,
  WEEKDAYS,
  YEAR_ONLY,
} from "shared/constants";
import { CurrencyCode } from "shared/types";
import { parseDateOnly, splitVisibleItems } from "shared/utils";

import { getDateFnsLocale } from "./getDateFnsLocale";
import { getUserLocale } from "./getUserLocale";

export type FormattedNumber = string | null;

// Null-fallback convention: the low-level numeric formatters (formatNumber,
// formatToShortNumber) return null for null/absent input and leave the display
// fallback to the caller — use `?? t("common:not_available")` rather than a
// hand-rolled string. The display-oriented formatCurrencyValue is the exception: it
// resolves the fallback itself (defaulting to common:not_available, overridable).
export const formatNumber = (
  n: number | null,
  numDecimalPlaces = 2,
  minDecimalPlaces = 0,
  locale: string = getUserLocale()
): FormattedNumber =>
  n === null
    ? null
    : new Intl.NumberFormat(locale, {
        maximumFractionDigits: numDecimalPlaces,
        minimumFractionDigits: minDecimalPlaces,
      }).format(n);

// A whole-number count with locale grouping (e.g. 1234 -> "1,234").
export const formatCount = (count: number | undefined): string =>
  formatNumber(count ?? null, 0) ?? i18n.t("common:not_available");

// formats an already-scaled percent value (e.g. 98.24 -> "98.24%")
export const formatPercentValue = (
  value: number,
  numDecimalPlaces = 2,
  locale: string = getUserLocale()
): string => `${formatNumber(value, numDecimalPlaces, 0, locale)}%`;

// formats a ratio as a percent (e.g. 0.9824 -> "98.24%")
export const formatPercent = (
  ratio: number,
  numDecimalPlaces = 2,
  locale: string = getUserLocale()
): string => formatPercentValue(ratio * 100, numDecimalPlaces, locale);

// A suffix that starts with one of these hugs the number ("98%", "21°C"); every other suffix gets a space ("12.5 km").
const NO_SPACE_SUFFIX = /^[%°]/;

// Intl's own number options plus a literal prefix and suffix for the unit or currency symbol, as an attribute's
// number format carries them. Intl throws on a contradictory pair such as min > max fraction digits; the API rejects
// that on write.
export const formatNumberWithOptions = (
  value: number,
  {
    prefix,
    suffix,
    ...intlOptions
  }: Intl.NumberFormatOptions & {
    prefix?: string | null;
    suffix?: string | null;
  },
  locale: string = getUserLocale()
): string => {
  const formatted = new Intl.NumberFormat(locale, intlOptions).format(value);
  const spacedSuffix = suffix
    ? NO_SPACE_SUFFIX.test(suffix)
      ? suffix
      : ` ${suffix}`
    : "";

  return `${prefix ?? ""}${formatted}${spacedSuffix}`;
};

const stringifyJson = (value: unknown): string | null => {
  try {
    return JSON.stringify(value) ?? null;
  } catch {
    return null;
  }
};

// The raw JSON text, or one field of it picked by an RFC 6901 pointer, where "" is the whole document and only null
// means no pointer. A json column arrives either as its text or already parsed. Invalid JSON and a pointer that finds
// nothing show the raw text; nothing here throws.
export const formatJsonValue = (
  value: unknown,
  pointer: string | null
): string | null => {
  const raw = typeof value === "string" ? value : stringifyJson(value);
  if (raw === null || pointer === null) return raw;

  try {
    const document = typeof value === "string" ? JSON.parse(value) : value;
    const target = getValueByPointer(document, pointer);
    if (target === undefined) return raw;

    return typeof target === "string" ? target : (stringifyJson(target) ?? raw);
  } catch {
    return raw;
  }
};

// The visible items joined with the separator, plus the caller's "+N more" text for the hidden ones.
export const formatStringArray = (
  items: string[],
  { separator, maxItems }: { separator: string; maxItems: number | null },
  moreLabel: (hiddenCount: number) => string
): string => {
  const { visible, hidden } = splitVisibleItems(items, maxItems);
  const text = visible.join(separator);

  if (hidden.length === 0) return text;

  return text
    ? `${text} ${moreLabel(hidden.length)}`
    : moreLabel(hidden.length);
};

// Each unit with the decimals it reads best at. KB and below are whole, since a tenth of a kilobyte is noise; MB and
// GB may show one. A round size keeps no trailing zero, so a limit still reads as "1 GB" rather than "1.0 GB".
const FILE_SIZE_UNITS = [
  { bytes: 1024 ** 3, key: "common:file_size.gb", decimals: 1 },
  { bytes: 1024 ** 2, key: "common:file_size.mb", decimals: 1 },
  { bytes: 1024, key: "common:file_size.kb", decimals: 0 },
];

// A byte count in the largest unit it fills (e.g. 1.5 MB). roundUp is for a size shown against a limit it exceeds:
// rounding down would print a file just over the cap as exactly the cap, so it rounds up at the unit's own precision.
export const formatBytes = (
  bytes: number,
  t: TFunction,
  roundUp = false
): string => {
  const unit = FILE_SIZE_UNITS.find(
    ({ bytes: unitBytes }) => bytes >= unitBytes
  );

  if (!unit) {
    return t("common:file_size.b", { size: formatNumber(bytes, 0) });
  }

  const size = bytes / unit.bytes;
  const step = 10 ** unit.decimals;

  return t(unit.key, {
    size: formatNumber(
      roundUp ? Math.ceil(size * step) / step : size,
      unit.decimals
    ),
  });
};

// target number of significant digits in compact form (e.g. "1.12M", "17.1M").
// the suffix (K/M/B/T for en, 万/億 for ja) itself also counts toward the
// maxDigits budget.
const COMPACT_SIGNIFICANT_DIGITS = 3;
// the compact suffix reserves one character of the maxDigits budget
const COMPACT_SUFFIX_CHARS = 1;

/**
 * Converts a formatted number string to short form if it exceeds `maxDigits`.
 * `maxDigits` counts the integer digits, fraction digits, and the compact
 * suffix together (so e.g. "1.2K" is 3 chars and "1.23K" is 4).
 */
export const formatToShortNumber = (
  formattedValue: FormattedNumber,
  maxDigits: number = 6,
  locale: string = getUserLocale()
): FormattedNumber => {
  if (!formattedValue) return null;

  const digitCount = formattedValue.replace(/[,.\-]/g, "").length;

  if (digitCount <= maxDigits) {
    return formattedValue;
  }

  const numericValue = parseFloat(formattedValue.replace(/,/g, ""));
  if (isNaN(numericValue)) return formattedValue;

  const absValue = Math.abs(numericValue);

  // number of integer digits the locale's compact form uses, derived from its
  // actual compact grouping (base-1000 K/M/B for en-US, base-10000 万/億 for
  // ja-JP). maximumFractionDigits is set high so the integer part is measured
  // before any rounding.
  const compactIntegerDigits = new Intl.NumberFormat(locale, {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 20,
  })
    .formatToParts(absValue)
    .filter((part) => part.type === "integer")
    .reduce((count, part) => count + part.value.length, 0);

  // target: aim for COMPACT_SIGNIFICANT_DIGITS total digits in the compact
  // form (e.g. "1.12M", "17.1M"), with a floor of 1 fraction digit so values
  // with 3 integer digits still show one decimal for readability ("101.2M"
  // rather than "101M").
  const targetFractionDigits = Math.max(
    1,
    COMPACT_SIGNIFICANT_DIGITS - compactIntegerDigits
  );
  // hard cap from the maxDigits budget: the integer digits and suffix each
  // consume part of the budget before any fraction digits are allowed
  const availableFractionDigits = Math.max(
    0,
    maxDigits - compactIntegerDigits - COMPACT_SUFFIX_CHARS
  );
  const fractionDigits = Math.min(
    availableFractionDigits,
    targetFractionDigits
  );

  return new Intl.NumberFormat(locale, {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: fractionDigits,
  }).format(numericValue);
};

export const currencyUnitToCurrencySign = (unit?: CurrencyCode): string => {
  switch (unit) {
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "JPY":
      return "¥";
    default:
      return "";
  }
};

// The euro prints after the amount; every other currency we support prints before it.
export const applyCurrencySign = (
  sign: string,
  formattedValue: FormattedNumber
): string =>
  sign === "€" ? `${formattedValue}${sign}` : `${sign}${formattedValue}`;

// The yen has no minor unit, so its amounts carry no decimals.
export const currencyDecimalPlaces = (sign: string): number | undefined =>
  sign === "¥" ? 0 : undefined;

export const formatCurrencyValue = (
  currencyCode: CurrencyCode,
  value: number | null | undefined,
  nullFallback: string = i18n.t("common:not_available"),
  decimalPlaces?: number,
  minDecimalPlaces: number = 2,
  locale: string = getUserLocale()
) => {
  if (value === null || value === undefined) return nullFallback;

  const sign = currencyUnitToCurrencySign(currencyCode);
  const signDecimalPlaces = currencyDecimalPlaces(sign);
  const maxDecimalPlaces = decimalPlaces ?? signDecimalPlaces;
  const minDecimalPlacesToApply = signDecimalPlaces ?? minDecimalPlaces;

  // min decimal places should not be higher than max decimal places, if it is, we set it to be the same as max decimal places
  const minDecimalPlacesToUse =
    maxDecimalPlaces !== undefined
      ? Math.min(minDecimalPlacesToApply, maxDecimalPlaces)
      : minDecimalPlacesToApply;

  const formattedNumber = formatNumber(
    value,
    maxDecimalPlaces,
    minDecimalPlacesToUse,
    locale
  );

  // show only number if no currency code
  if (!currencyCode) return formattedNumber;

  return applyCurrencySign(sign, formattedNumber);
};

const SPECIAL_DATE_VALUES = [MAX_FILTER_ID, MIN_FILTER_ID];

// The zoned variant below is this plus the zone name, so they share one source
// to keep the time part identical.
const DATE_WITH_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
};

const { second, ...DATE_WITH_TIME_NO_SECONDS_OPTIONS } = DATE_WITH_TIME_OPTIONS;

const DATE_FORMAT_OPTIONS: Record<
  DisplayDateFormat,
  Intl.DateTimeFormatOptions
> = {
  [LONG_DATE_FORMAT]: { year: "numeric", month: "long", day: "numeric" },
  [SHORT_DATE_FORMAT]: { year: "numeric", month: "2-digit", day: "2-digit" },
  [VERY_SHORT_DATE_FORMAT]: {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
  },
  [MONTH_YEAR]: { year: "numeric", month: "short" },
  [MONTH_YEAR_SHORT]: { year: "2-digit", month: "short" },
  [MONTH_YEAR_FULL]: { year: "numeric", month: "long" },
  [MONTH_YEAR_DAY]: { year: "numeric", month: "short", day: "numeric" },
  [MONTH_DAY]: { month: "short", day: "numeric" },
  [MONTH_DAY_NUMERIC]: { month: "numeric", day: "numeric" },
  [MONTH_ONLY]: { month: "short" },
  [YEAR_ONLY]: { year: "numeric" },
  [HOUR_ONLY]: { hour: "numeric", hour12: true },
  [TIME_ONLY]: { hour: "numeric", minute: "2-digit", hour12: true },
  [TIME_WITH_SECONDS]: {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  },
  [DATE_WITH_TIME_FORMAT]: DATE_WITH_TIME_OPTIONS,
  [DATE_WITH_TIME_NO_SECONDS]: DATE_WITH_TIME_NO_SECONDS_OPTIONS,
  [DATE_WITH_TIME_AND_ZONE_FORMAT]: {
    ...DATE_WITH_TIME_OPTIONS,
    timeZoneName: "short",
  },
};

const DISPLAY_DATE_FORMAT_SET = new Set<string>(DISPLAY_DATE_FORMATS);

export const isDisplayDateFormat = (
  format: string
): format is DisplayDateFormat => DISPLAY_DATE_FORMAT_SET.has(format);

const getZoneName = (
  formatter: Intl.DateTimeFormat,
  date: Date
): string | undefined =>
  formatter.formatToParts(date).find((part) => part.type === "timeZoneName")
    ?.value;

// Intl has no option for punctuating the zone name, so formats that ask for one
// get it wrapped in parentheses here ("5:00:00 PM (PDT)"). Only the zone
// substring is swapped into format()'s own output rather than rejoining
// formatToParts(): the parts keep the narrow no-break space before AM/PM that
// format() normalizes to a plain space, so rebuilding the string would make
// these dates differ invisibly from every other date in the app.
const formatWithParenthesizedZone = (
  formatter: Intl.DateTimeFormat,
  date: Date
): string => {
  const formatted = formatter.format(date);
  const zone = getZoneName(formatter, date);

  return zone ? formatted.replace(zone, `(${zone})`) : formatted;
};

const dateTimeFormatterCache = new Map<string, Intl.DateTimeFormat>();

const getDateTimeFormatter = (
  locale: string,
  formatKey: DisplayDateFormat,
  timeZone?: "UTC"
): Intl.DateTimeFormat => {
  const cacheKey = `${locale}|${formatKey}|${timeZone ?? "local"}`;
  const cached = dateTimeFormatterCache.get(cacheKey);
  if (cached) return cached;

  const formatter = new Intl.DateTimeFormat(locale, {
    ...DATE_FORMAT_OPTIONS[formatKey],
    ...(timeZone ? { timeZone } : {}),
  });
  dateTimeFormatterCache.set(cacheKey, formatter);

  return formatter;
};

export const formatRelativeTime = (
  value: string | number | Date,
  locale: string = getUserLocale()
): string =>
  formatDistance(new Date(value), new Date(), {
    addSuffix: true,
    locale: getDateFnsLocale(locale),
  });

export const formatDate = (
  value: string | number,
  dateFormat: string = LONG_DATE_FORMAT, // April 28, 2022
  ignoreTime: boolean = false,
  timeAgoWhenRecent: boolean = false,
  timeZone?: "UTC",
  locale: string = getUserLocale()
): string => {
  // numeric (epoch) labels must stay numeric for new Date() — "1641024000000" parses as invalid
  const numericDate = typeof value === "number" ? new Date(value) : undefined;
  const str =
    numericDate && isValid(numericDate)
      ? numericDate.toISOString()
      : String(value ?? "");

  // don't format null dates
  if (!str || str === "null") return "";

  // Last Detected At can have special values
  if (SPECIAL_DATE_VALUES.includes(str)) {
    return str;
  }

  try {
    if (
      timeAgoWhenRecent &&
      Math.abs(differenceInHours(new Date(str), new Date())) <
        TIME_AGO_THRESHOLD_HOURS
    ) {
      return formatRelativeTime(str, locale);
    }

    const date = ignoreTime ? parseDateOnly(str) : new Date(str);
    if (!isValid(date)) {
      console.error(`Invalid date in formatDate: "${str}"\n`);

      return str;
    }

    if (dateFormat === ISO_FORMAT) {
      return date.toISOString();
    }

    if (isDisplayDateFormat(dateFormat)) {
      const formatter = getDateTimeFormatter(
        locale,
        dateFormat,
        ignoreTime ? undefined : timeZone
      );

      return DATE_FORMAT_OPTIONS[dateFormat].timeZoneName
        ? formatWithParenthesizedZone(formatter, date)
        : formatter.format(date);
    }

    // A template outside the display formats (an attribute's stored dateFormat) still gets the user's month and day
    // names.
    return format(date, dateFormat, { locale: getDateFnsLocale(locale) });
  } catch (err) {
    console.error(err);

    return str;
  }
};

// The short zone name on its own ("PDT"), for callers that label a whole range
// rather than each timestamp in it. Returns "" for an unparseable value.
export const formatTimeZoneName = (
  value: string | number,
  locale: string = getUserLocale()
): string => {
  const date = new Date(value);
  if (!isValid(date)) return "";

  return (
    getZoneName(
      getDateTimeFormatter(locale, DATE_WITH_TIME_AND_ZONE_FORMAT),
      date
    ) ?? ""
  );
};

export const formatHourOfDay = (hour: number): string => {
  const d = new Date(2000, 0, 1, hour, 0, 0);

  return new Intl.DateTimeFormat(getUserLocale(), {
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
};

export const formatWeekday = (weekday: Weekday): string => {
  // Jan 3 2000 was a Monday — offset by day index to get Mon–Sun dates.
  const d = new Date(2000, 0, 3 + WEEKDAYS.indexOf(weekday));

  return new Intl.DateTimeFormat(getUserLocale(), {
    weekday: "long",
  }).format(d);
};

// A compact label for a weekday toggle (e.g. "Mo", "Tu"). Intl's "short"
// form is the shortest unambiguous option ("Mon", "Tue", ...); trimming it
// to 2 characters keeps that same locale-aware text but tighter, and is a
// no-op for locales whose short form is already 1-2 characters (e.g. ja).
export const formatWeekdayShort = (weekday: Weekday): string => {
  const d = new Date(2000, 0, 3 + WEEKDAYS.indexOf(weekday));

  return new Intl.DateTimeFormat(getUserLocale(), { weekday: "short" })
    .format(d)
    .slice(0, 2);
};

// locale-aware list joining, e.g. "Monday, Wednesday, and Friday" in en-US,
// "月曜日、水曜日、金曜日" in ja-JP — a plain .join(", ") reads as broken in
// locales that don't use a Western comma-space separator.
export const formatList = (
  items: string[],
  locale: string = getUserLocale()
): string =>
  new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(
    items
  );

// Ordinal suffix per Intl.PluralRules category, keyed by language. Japanese
// has no ordinal categories beyond "other" — CLDR just counts days with "日".
const DAY_OF_MONTH_SUFFIX: Record<string, Partial<Record<string, string>>> = {
  en: { one: "st", two: "nd", few: "rd", other: "th" },
  ja: { other: "日" },
};

export const formatDayOfMonth = (dayOfMonth: number): string => {
  const locale = getUserLocale();
  const language = locale.split("-")[0];
  const category = new Intl.PluralRules(locale, { type: "ordinal" }).select(
    dayOfMonth
  );
  const suffixes = DAY_OF_MONTH_SUFFIX[language] ?? DAY_OF_MONTH_SUFFIX.en;

  return `${dayOfMonth}${suffixes[category] ?? DAY_OF_MONTH_SUFFIX.en.other}`;
};
